import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../../store/slices/authSlice";
import MenuIcon from "@mui/icons-material/Menu";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { CaretDownFill } from "react-bootstrap-icons";
import { broadcastThisMessage } from "../../../store/slices/broadcastMessageSlice";
import { useFormik } from "formik";
import { ArrowClockwise, Broadcast, Check } from "react-bootstrap-icons";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import BroadcastMessage from "../../../components/BroadcastMessage/BroadcastMessage";
import BroadcastMessageAdmin from "../../../components/BroadcastMessage/BroadcastMessageAdmin";

const Navbar = ({ setIsSidebarOpenMobile }) => {
  const { user, isRoot, otherUser } = useSelector((state) => state.auth);
  const showSwitchStation = otherUser && (otherUser.attributes?.roles || []).includes('group_admin');
  const dispatch = useDispatch(); // Hook to dispatch actions
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [error, setError] = useState(null);
  const handleLogout = () => {
    dispatch(logout());
  };

  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };
  const formik = useFormik({
    initialValues: {
      message: ""
    },
    onSubmit: async (values, { resetForm }) => {
      setIsBroadcasting(true);
      setError(null);
      try {
        const resultAction = await dispatch(broadcastThisMessage({ broadcast_msg: values.message }));

        if (broadcastThisMessage.fulfilled.match(resultAction)) {
          resetForm();
          setShowBroadcastModal(false);
        } else {
          throw new Error(resultAction.error.message || "Failed to broadcast message");
        }
      } catch (error) {
        console.error("Broadcast failed:", error);
        setError(error.message || "An error occurred while broadcasting");
      } finally {
        setIsBroadcasting(false);
      }
    }
  });

  return (
    <>
      <nav
        className="dashboard-navbar"
        style={{
          borderBottom: `5px solid ${user?.attributes.stationTheme || ""}`,
        }}
      >
        {/* Broadcast Modal */}
        <Modal show={showBroadcastModal} onHide={() => !isBroadcasting && setShowBroadcastModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Create Broadcast Message</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && (
              <Alert variant="danger" onClose={() => setError(null)} dismissible>
                {error}
              </Alert>
            )}
            <Form onSubmit={formik.handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="message"
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  placeholder="Enter your broadcast message here..."
                  disabled={isBroadcasting}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowBroadcastModal(false)}
              disabled={isBroadcasting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={formik.handleSubmit}
              disabled={isBroadcasting || !formik.values.message}
            >
              {isBroadcasting ? (
                <>
                  <ArrowClockwise className="spin-animation me-1" size={14} />
                  Sending...
                </>
              ) : (
                <>
                  <Broadcast className="me-1" size={14} />
                  Broadcast
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        <div className="navbar-content">
          {/* Hamburger menu for mobile */}
          <button
            className="navbar-hamburger"
            onClick={() => setIsSidebarOpenMobile(true)}
            style={{
              background: "none",
              border: "none",
              display: "flex",
              alignItems: "center",
              marginRight: 12,
              fontSize: 28,
              cursor: "pointer",
            }}
          >
            <MenuIcon fontSize="large" />
          </button>
          <div className="navbar-logo">
            <img
              src={user?.attributes.stationLogo || "logo"}
              alt="Station Logo"
              className="logo-image"
            />
          </div>
          {/* Broadcast Message Section */}
          {isRoot ? (<div className="me-3 flex-grow-1 d-flex justify-content-end">
            <div className="d-flex align-items-center">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => setShowBroadcastModal(true)}
                style={{ minWidth: "150px" }}
              >
                <Broadcast className="me-1" size={14} />
                Send Broadcast Message
              </button>
            </div>
          </div>) : "" }
          {showSwitchStation && (
            <Link
              to="/admin/station-groups"
              className="btn btn-sm btn-outline-primary d-flex align-items-center me-2"
              style={{ minWidth: "120px" }}
            >
              <SwapHorizIcon fontSize="small" className="me-1" />
              Switch station
            </Link>
          )}
          <div className="navbar-user">
            <div className="d-flex align-items-center">
              <span className="me-2">{user?.attributes.firstName} {user?.attributes.lastName ? user?.attributes.lastName.split("")[0] : ""}</span>
              <div className="dropdown">
                <button
                  className="btn dropdown-toggle d-flex align-items-center"
                  type="button"
                  id="userDropdown"
                  onClick={toggleDropdown}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '0'
                  }}
                >
                  <div
                    className="rounded-circle bg-secondary text-white text-center d-flex justify-content-center align-items-center me-1"
                    style={{
                      width: "35px",
                      height: "35px",
                      lineHeight: "35px",
                      cursor: "pointer"
                    }}
                  >
                    {user?.attributes.lastName ? user?.attributes.lastName.charAt(0) : "G"}
                  </div>
                  <CaretDownFill size={12} />
                </button>
                {isDropdownOpen && (
                  <ul
                    className="dropdown-menu show"
                    aria-labelledby="userDropdown"
                    style={{
                      position: 'absolute',
                      right: 0,
                      left: 'auto'
                    }}
                  >
                    <li>
                      <button
                        className="dropdown-item d-flex justify-content-between align-items-center"
                        onClick={handleLogout}
                      >
                        <span>Logout</span>
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </div>
          
        </div>

      </nav>
      
      <style jsx>{`
                .spin-animation {
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .dropdown-toggle::after {
                    display:none;
                }
                .broadCastDiv{
                  position: fixed;
                  top: 84px;
                  left: 0;
                  right: 0;
                  z-index: 1001;
                }
            `}</style>
    </>
  );
};

export default Navbar;
